import React, { useState } from 'react';
import {
  View,
  Image,
  TextInput,
  Pressable,
  TouchableWithoutFeedback,
  Keyboard,
  StyleSheet,
} from 'react-native';
import * as Auth from 'aws-amplify/auth';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import AppButton from '../components/AppButton';
import alert from '../components/alert';
import CustomText from '../components/CustomText';
import { useFormValidation, validateEmail } from '../util/validation/formValidator';
import { useResponsiveStyles } from '../hooks';
import AppStyles from '../AppStyles';
import { fonts } from '../style/fonts';
import { useTranslation } from '../i18n';

const ForgotPassword = (props) => {
  const { t } = useTranslation('auth');

  const { isWeb, isDesktop, containerStyle } = useResponsiveStyles();

  // Focus state for V3 input styling
  const [emailFocused, setEmailFocused] = useState(false);

  // Form validation using centralized hook
  const {
    values,
    isValid,
    setDisplayErrors,
    setFieldValue,
    getFieldError,
  } = useFormValidation(
    { email: '' },
    { email: [validateEmail] }
  );

  async function handleForgotPassword() {
    setDisplayErrors(true);
    if (!isValid) {
      return;
    }

    try {
      await Auth.resetPassword({ username: values.email });

      Toast.show(`✅ ${t('forgot.codeSent')}`, {
        backgroundColor: '#259e23',
        duration: 5000,
        textColor: '#fff',
      });
      props.navigation.navigate('ResetPassword');
    } catch (error) {
      console.log(
        '❌ Verification code does not match. Please enter a valid verification code.',
        error.toString(),
      );

      alert(t('common:actions.error'), '❌' + error.toString());
    }
  }

  // Responsive widths
  const logoWidth = isDesktop ? 320 : 240;
  const logoHeight = isDesktop ? 89 : 67;
  const cardWidth = isDesktop ? wp(45) : wp(90);
  const linkFontSize = isDesktop ? 18 : 16;

  return (
    <TouchableWithoutFeedback
      onPress={!isWeb ? Keyboard.dismiss : () => {}}
      accessible={false}
    >
      <View style={[containerStyle, styles.container]}>
        {/* Gold accent bar at top */}
        <View style={styles.accentBar} />

        {/* Logo */}
        <Image
          source={require('../assets/images/logo-black.png')}
          style={[styles.logo, { width: logoWidth, height: logoHeight }]}
        />

        {/* Tagline */}
        <CustomText
          style={styles.tagline}
          font="regular"
          size={16}
          color={AppStyles.colorSet.textSecondary}
        >
          {t('forgot.title')}
        </CustomText>

        {/* Form Card */}
        <View style={[styles.formCard, { width: cardWidth }]}>
          {/* Instructions */}
          <CustomText
            style={styles.instructions}
            font="regular"
            size={14}
            color={AppStyles.colorSet.textSecondary}
          >
            {t('forgot.description')}
          </CustomText>

          {/* Email input - V3 style */}
          <View
            style={[
              styles.inputContainer,
              emailFocused && styles.inputContainerFocused,
              { marginTop: hp(2) },
            ]}
          >
            <Feather
              name="mail"
              size={20}
              color={emailFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
              style={styles.inputIcon}
            />
            <TextInput
              value={values.email}
              onChangeText={(text) => setFieldValue('email', text)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              placeholder={t('forgot.emailPlaceholder')}
              placeholderTextColor={AppStyles.colorSet.textMuted}
              style={styles.inputText}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              textContentType="emailAddress"
            />
          </View>
          {getFieldError('email') && (
            <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
              {getFieldError('email')}
            </CustomText>
          )}

          {/* Send Reset Code button - V3 style */}
          <View style={{ marginTop: hp(3) }}>
            <AppButton
              title={t('forgot.sendCode')}
              isEnabled={isValid}
              onPress={handleForgotPassword}
              fullWidth
            />
          </View>
        </View>

        {/* Back to login link */}
        <View style={styles.linksContainer}>
          <Pressable
            onPress={() => props.navigation.navigate('Login')}
            style={[styles.link, { cursor: 'pointer' }]}
          >
            {({ hovered, focused }) => (
              <>
                <CustomText font="regular" size={linkFontSize} color={AppStyles.colorSet.textSecondary}>
                  {t('forgot.rememberPassword')}
                </CustomText>
                <CustomText
                  font="semiBold"
                  size={linkFontSize}
                  color={AppStyles.colorSet.brandPrimary}
                  style={(hovered || focused) && { textDecorationLine: 'underline' }}
                >
                  {t('forgot.signIn')}
                </CustomText>
              </>
            )}
          </Pressable>
        </View>
      </View>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: wp(5),
    paddingTop: hp(2),
    paddingBottom: hp(4),
  },
  accentBar: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: 4,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  logo: {
    resizeMode: 'contain',
    marginTop: hp(6),
  },
  tagline: {
    marginTop: hp(1),
    marginBottom: hp(4),
  },
  formCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.04,
    shadowRadius: 8,
    elevation: 2,
  },
  instructions: {
    textAlign: 'center',
    lineHeight: 22,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  inputContainerFocused: {
    backgroundColor: AppStyles.colorSet.inputBackgroundFocused,
    borderWidth: 2,
    borderColor: AppStyles.colorSet.inputBorderFocused,
    paddingHorizontal: 14,
    paddingVertical: 12,
  },
  inputIcon: {
    marginRight: 12,
  },
  inputText: {
    flex: 1,
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
  errorText: {
    marginTop: 4,
    marginLeft: 4,
  },
  linksContainer: {
    alignItems: 'center',
    marginTop: hp(4),
  },
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    cursor: 'pointer',
  },
});

export default ForgotPassword;
